<script setup>
import { useUserStore } from '@/stores/user';
import userAvatar from '@/components/userAvatar.vue';
import { computed, ref, onMounted } from 'vue';
import { useFunctionsStore } from '@/stores/functions';
const functionsStore = useFunctionsStore();
import { useRouter } from 'vue-router';
const router = useRouter();
const userStore = useUserStore();

const widgetPrefs = computed(() => userStore.preferences.widgets.dashboard);
const widgetSettings = computed(() => userStore.preferences.widgetSettings || {});
const widgetOrder = computed(() => userStore.preferences.widgetOrder || []);
const notifPrefs = computed(() => userStore.preferences.notifications);
const uiPrefs = computed(() => userStore.preferences.ui);
const privacyPrefs = computed(() => userStore.preferences.privacy);

function toggleWidget(key) { userStore.setWidgetPreference('dashboard', key, !widgetPrefs.value[key]); }
function updatePreference(group, key, value) { userStore.setPreference(group, key, value); }
function updateWidgetSetting(widget, key, value) { userStore.setWidgetConfig(widget, { [key]: value }); }
function moveWidget(oldIndex, newIndex) {
  const arr = widgetOrder.value.slice();
  if (newIndex < 0 || newIndex >= arr.length) return;
  const [it] = arr.splice(oldIndex,1);
  arr.splice(newIndex,0,it);
  userStore.setWidgetOrder(arr);
}

// Export / Import
const exportText = ref('');
function doExport() { exportText.value = userStore.exportPreferences(); }
function doReset() { if (confirm('Alle Einstellungen wirklich zurücksetzen?')) { userStore.resetPreferences(); doExport(); } }
function doImport() { if (!exportText.value) return; const ok = userStore.importPreferences(exportText.value); if (!ok) alert('Import fehlgeschlagen: Ungültiges JSON'); }

function labelFor(key) {
  const map = { showIncentives: 'Champions anzeigen', showPerformance: 'Performance anzeigen', showActivity: 'Aktivitäten anzeigen', showRatings: 'Bewertungen anzeigen', compactTables: 'Kompakte Tabellen' };
  return map[key] || key;
}

const initials = computed(() => {
  if (!userStore.firstName && !userStore.lastName) return '?';
  return `${(userStore.firstName||'')[0]||''}${(userStore.lastName||'')[0]||''}`.toUpperCase();
});

const heroStyle = computed(() => ({ marginTop: `${(functionsStore.navigationHeight||0)+8}px` }));

const widgetMeta = {
  incentives: { label: 'Champions', desc: 'Rangliste & Bonus Übersicht' },
  performance: { label: 'Performance', desc: 'Ziele, Umsätze & Fortschritt' },
  potentialBk: { label: 'Potential & BK', desc: 'Lead-Potential & Bestandkunden KPIs' },
  activity: { label: 'Aktivitäten', desc: 'Anrufe, Termine & Follow-Ups' },
  ratings: { label: 'Bewertungen', desc: 'Kundenfeedback & Sterne' }
};

const roleLabel = computed(() => {
  if (userStore.isManagement) return 'Management';
  if (userStore.isSalesTeamManager) return 'Sales Team Lead';
  if (userStore.isSalesAgent) return 'Sales Agent';
  return 'User';
});
// Chips with contextual info (campaign, company, team, area)
const heroChips = computed(() => {
  const chips = [];
  const camp = userStore.currentSelection?.campaign;
  if (camp?.name) chips.push({ k: 'kampagne', label: 'Kampagne', value: camp.name });
  if (camp?.company?.name) chips.push({ k: 'company', label: 'Company', value: camp.company.name });
  // derive team name from campaign roles if available
  const teamRole = camp?.roles?.find(r => r?.team?.name);
  if (teamRole?.team?.name) chips.push({ k: 'team', label: 'Team', value: teamRole.team.name });
  const salesAgentRole = camp?.roles?.find(r => r?.salesAgent);
  const agent = salesAgentRole?.salesAgent;
  const area = agent?.area || agent?.region || agent?.territory;
  if (area) chips.push({ k: 'area', label: 'Area', value: area });
  return chips;
});

const openDetails = ref({});
function toggleDetails(k){ openDetails.value[k] = !openDetails.value[k]; }
</script>

<template>
  <div class="settings-page padded">
  <div class="settings-back padded top-back"><router-link to="/dashboard" class="back-btn">← Zurück zum Dashboard</router-link></div>
  <div class="hero max-width rounded" :style="heroStyle">
      <div class="hero-content">
        <div class="avatar-shell" v-if="userStore.preferences.privacy.showAvatar">
          <userAvatar :salesAgentId="userStore.currentSelection?.salesAgent?.id" :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback" />
        </div>
        <div v-else class="avatar-fallback">{{ initials }}</div>
        <div class="user-meta">
          <h1>Einstellungen</h1>
          <p class="username">{{ userStore.firstName }} {{ userStore.lastName }} <span class="role-badge">{{ roleLabel }}</span></p>
          <p v-if="userStore.preferences.privacy.showEmail" class="email">{{ userStore.emailAddress }}</p>
          <ul class="chip-row" v-if="heroChips.length">
            <li v-for="c in heroChips" :key="c.k" class="pill hero-pill">
              <span class="c-label">{{ c.label }}</span>
              <span class="c-val">{{ c.value }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <main class="content max-width">
      <!-- Globale Widget Sichtbarkeit -->
      <section class="panel panel-global-widgets" id="global-widgets">
        <header>
          <h2>Widget Übersicht</h2>
          <p>Schnellsteuerung für Sichtbarkeit & Tabellenmodus.</p>
        </header>
        <div class="toggle-group pills">
          <button type="button" class="pill" :class="{active: widgetPrefs.showIncentives}" @click="toggleWidget('showIncentives')">Champions</button>
          <button type="button" class="pill" :class="{active: widgetPrefs.showPerformance}" @click="toggleWidget('showPerformance')">Performance</button>
          <button type="button" class="pill" :class="{active: widgetPrefs.showPotentialBk}" @click="toggleWidget('showPotentialBk')">Potential & BK</button>
          <button type="button" class="pill" :class="{active: widgetPrefs.showActivity}" @click="toggleWidget('showActivity')">Aktivitäten</button>
          <button type="button" class="pill" :class="{active: widgetPrefs.showRatings}" @click="toggleWidget('showRatings')">Bewertungen</button>
          <span class="pill-separator"></span>
          <button type="button" class="pill mode" :class="{active: widgetPrefs.compactTables}" @click="toggleWidget('compactTables')">Kompakte Tabellen</button>
        </div>
      </section>
      <!-- Einzelne Widget Panels -->
      <!-- Einzelne Widget Panels -->
      <section class="panel widget-panel" v-for="(wid, idx) in widgetOrder" :key="'panel-'+wid">
        <header class="w-head">
          <div class="h-titles">
            <h2>{{ widgetMeta[wid]?.label || wid }}</h2>
            <p>{{ widgetMeta[wid]?.desc }}</p>
          </div>
          <div class="w-actions">
            <button class="w-move" @click="moveWidget(idx, idx-1)" :disabled="idx===0" title="Nach oben">↑</button>
            <button class="w-move" @click="moveWidget(idx, idx+1)" :disabled="idx===widgetOrder.length-1" title="Nach unten">↓</button>
            <label class="switch sm" :title="'Sichtbarkeit'">
              <input type="checkbox" :checked="widgetPrefs['show'+(wid.charAt(0).toUpperCase()+wid.slice(1))] || (wid==='incentives' ? widgetPrefs.showIncentives : wid==='potentialBk' ? widgetPrefs.showPotentialBk : false)" @change="toggleWidget(wid==='incentives' ? 'showIncentives' : wid==='performance' ? 'showPerformance' : wid==='potentialBk' ? 'showPotentialBk' : wid==='activity' ? 'showActivity' : 'showRatings')">
              <span class="slider"></span>
            </label>
          </div>
        </header>
        <div class="w-body">
          <template v-if="wid==='incentives'">
            <label>Top hervorheben
              <input type="number" min="1" max="10" :value="widgetSettings.incentives?.highlightTop" @change="updateWidgetSetting('incentives','highlightTop', Number($event.target.value))" />
            </label>
            <label class="inline-row">Trend anzeigen
              <input type="checkbox" :checked="widgetSettings.incentives?.showTrend" @change="updateWidgetSetting('incentives','showTrend', !widgetSettings.incentives?.showTrend)" />
            </label>
          </template>
          <template v-else-if="wid==='performance'">
            <label class="inline-row">Ziele anzeigen
              <input type="checkbox" :checked="widgetSettings.performance?.showTargets" @change="updateWidgetSetting('performance','showTargets', !widgetSettings.performance?.showTargets)" />
            </label>
            <label class="inline-row">Delta anzeigen
              <input type="checkbox" :checked="widgetSettings.performance?.showDelta" @change="updateWidgetSetting('performance','showDelta', !widgetSettings.performance?.showDelta)" />
            </label>
          </template>
          <template v-else-if="wid==='potentialBk'">
            <label class="inline-row">Lead-Funnel anzeigen
              <input type="checkbox" :checked="widgetSettings.potentialBk?.showFunnel" @change="updateWidgetSetting('potentialBk','showFunnel', !widgetSettings.potentialBk?.showFunnel)" />
            </label>
            <label class="inline-row">Bestandskunden KPIs
              <input type="checkbox" :checked="widgetSettings.potentialBk?.showBkKPIs" @change="updateWidgetSetting('potentialBk','showBkKPIs', !widgetSettings.potentialBk?.showBkKPIs)" />
            </label>
            <label>Zeitraum
              <select :value="widgetSettings.potentialBk?.period || 'month'" @change="updateWidgetSetting('potentialBk','period', $event.target.value)">
                <option value="week">Woche</option>
                <option value="month">Monat</option>
                <option value="quarter">Quartal</option>
              </select>
            </label>
            <label>Benchmark
              <input type="number" min="0" :value="widgetSettings.potentialBk?.benchmark || 0" @change="updateWidgetSetting('potentialBk','benchmark', Number($event.target.value))" />
            </label>
          </template>
          <template v-else-if="wid==='activity'">
            <label>Diagramm-Typ
              <select :value="widgetSettings.activity?.chartType" @change="updateWidgetSetting('activity','chartType', $event.target.value)">
                <option value="bar">Balken</option>
                <option value="line">Linie</option>
              </select>
            </label>
            <label>Zeitraum
              <select :value="widgetSettings.activity?.period" @change="updateWidgetSetting('activity','period', $event.target.value)">
                <option value="day">Tag</option>
                <option value="week">Woche</option>
                <option value="month">Monat</option>
              </select>
            </label>
          </template>
            <template v-else-if="wid==='ratings'">
              <label>Min. Sterne
                <input type="number" min="1" max="5" :value="widgetSettings.ratings?.minStars" @change="updateWidgetSetting('ratings','minStars', Number($event.target.value))" />
              </label>
            </template>
          <div class="foot-hint">Widget-Key: <code>{{ wid }}</code></div>
        </div>
      </section>

      <!-- Notifications -->
      <section class="panel" id="notifications">
        <header><h2>Benachrichtigungen</h2></header>
        <div class="form-grid">
          <label>Erfolgs-Meldungen automatisch ausblenden
            <input type="checkbox" :checked="notifPrefs.autoDismissSuccess" @change="updatePreference('notifications','autoDismissSuccess', !notifPrefs.autoDismissSuccess)">
          </label>
          <label>Auto-Dismiss Verzögerung (ms)
            <input type="number" min="500" step="100" :value="notifPrefs.autoDismissDelayMs" @change="updatePreference('notifications','autoDismissDelayMs', Number($event.target.value))">
          </label>
          <label>Max. sichtbare Meldungen
            <input type="number" min="1" max="20" :value="notifPrefs.maxVisible" @change="updatePreference('notifications','maxVisible', Number($event.target.value))">
          </label>
          <label class="inline-flex">Sound abspielen
            <input type="checkbox" :checked="notifPrefs.playSound" @change="updatePreference('notifications','playSound', !notifPrefs.playSound)">
          </label>
          <label>Lautstärke
            <input type="range" min="0" max="1" step="0.05" :value="notifPrefs.soundVolume" @input="updatePreference('notifications','soundVolume', Number($event.target.value))">
          </label>
        </div>
      </section>

      <!-- Interface -->
      <section class="panel" id="ui">
        <header><h2>Interface</h2></header>
        <div class="form-grid">
          <label>Dichte
            <select :value="uiPrefs.density" @change="updatePreference('ui','density',$event.target.value)">
              <option value="comfortable">Komfortabel</option>
              <option value="compact">Kompakt</option>
            </select>
          </label>
          <label>Sprache
            <select :value="uiPrefs.language" @change="updatePreference('ui','language',$event.target.value)">
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </label>
          <label>Theme
            <select :value="uiPrefs.theme" @change="updatePreference('ui','theme',$event.target.value)">
              <option value="light">Hell</option>
              <option value="dark">Dunkel</option>
            </select>
          </label>
        </div>
      </section>

      <!-- Privacy -->
      <section class="panel" id="privacy">
        <header><h2>Privatsphäre</h2></header>
        <ul class="card-list">
          <li class="card-row">
            <div class="row-main">
              <div class="row-label">Performance öffentlich teilen</div>
              <div class="row-desc">Ermöglicht Kollegen deine Rankings zu sehen.</div>
            </div>
            <div class="row-action"><input type="checkbox" :checked="privacyPrefs.sharePerformancePublic" @change="updatePreference('privacy','sharePerformancePublic', !privacyPrefs.sharePerformancePublic)"></div>
          </li>
          <li class="card-row">
            <div class="row-main"><div class="row-label">E-Mail anzeigen</div></div>
            <div class="row-action"><input type="checkbox" :checked="privacyPrefs.showEmail" @change="updatePreference('privacy','showEmail', !privacyPrefs.showEmail)"></div>
          </li>
          <li class="card-row">
            <div class="row-main"><div class="row-label">Avatar anzeigen</div></div>
            <div class="row-action"><input type="checkbox" :checked="privacyPrefs.showAvatar" @change="updatePreference('privacy','showAvatar', !privacyPrefs.showAvatar)"></div>
          </li>
        </ul>
      </section>

      <!-- Data -->
      <section class="panel" id="data">
        <header><h2>Daten & Export</h2></header>
        <div class="export-actions">
          <button class="btn" @click="doExport">Exportieren</button>
          <button class="btn secondary" @click="doImport">Importieren</button>
          <button class="btn danger" @click="doReset">Zurücksetzen</button>
        </div>
        <textarea class="export-area" v-model="exportText" placeholder="JSON Einstellungen hier..." rows="10"></textarea>
        <p class="hint">Exportiere Einstellungen oder übertrage sie auf ein anderes Gerät.</p>
      </section>
    </main>
  </div>
</template>

<style scoped>
.settings-page { --panel-bg: rgba(255,255,255,0.04); padding-bottom:4rem; }
.theme--light .settings-page { --panel-bg:#fff; }

.hero { padding:2.9rem 2.2rem 2.4rem; display:flex; flex-direction:column; gap:1.6rem; }
.hero { background:linear-gradient(140deg,color-mix(in srgb,var(--sellwerk-color-main) 65%, #ffffff) 0%, color-mix(in srgb,var(--sellwerk-color-main) 45%, #0a0a0a) 65%, rgba(0,0,0,.55) 120%); border:1px solid color-mix(in srgb,var(--sellwerk-color-main) 35%, #ffffff); box-shadow:0 14px 48px -18px rgba(0,0,0,.55),0 5px 18px -8px rgba(0,0,0,.35); position:relative; overflow:hidden; }
.hero:before { content:""; position:absolute; inset:0; background:radial-gradient(circle at 82% 24%, color-mix(in srgb,var(--sellwerk-color-main) 40%, #ffffff) 0%, transparent 60%); mix-blend-mode:overlay; opacity:.4; pointer-events:none; }
.theme--light .hero { background:linear-gradient(150deg,color-mix(in srgb,var(--sellwerk-color-main) 90%, #ffffff) 0%, color-mix(in srgb,var(--sellwerk-color-main) 30%, #ffffff) 75%); border-color:color-mix(in srgb,var(--sellwerk-color-main) 45%, #ffffff); box-shadow:0 20px 46px -26px color-mix(in srgb,var(--sellwerk-color-main) 40%, #00000040),0 4px 18px -10px rgba(0,0,0,.18); }
.settings-back.top-back { margin-top: calc((var(--nav-height, 0px)) + .75rem); margin-bottom: .75rem; }
.chip-row { list-style:none; padding:0; margin:.9rem 0 0; display:flex; flex-wrap:wrap; gap:.5rem; }
.hero-pill { font-size:.52rem; padding:.4rem .7rem; letter-spacing:.07em; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.22); cursor:default; }
.hero-pill .c-label { opacity:.65; }
.hero-pill .c-val { font-weight:600; }
.theme--light .hero-pill { background:rgba(0,0,0,.05); border-color:rgba(0,0,0,.15); }
@media (max-width:780px){ .settings-back.top-back { margin-top: .5rem; } }
.hero-top { display:flex; justify-content:flex-start; }
.back-btn { font-size:.75rem; text-decoration:none; background:color-mix(in srgb,var(--sellwerk-color-main) 35%, #ffffff10); padding:.55rem .85rem; border-radius:10px; color:#fff; letter-spacing:.55px; font-weight:600; backdrop-filter:blur(8px); border:1px solid color-mix(in srgb,var(--sellwerk-color-main) 55%, #ffffff15); box-shadow:0 2px 6px -2px rgba(0,0,0,.5); transition:.25s; }
.back-btn:hover { background:color-mix(in srgb,var(--sellwerk-color-main) 55%, #ffffff18); }
.theme--light .back-btn { background:color-mix(in srgb,var(--sellwerk-color-main) 20%, #ffffff); color:#102433; border:1px solid color-mix(in srgb,var(--sellwerk-color-main) 35%, #ffffff); }
.theme--light .back-btn:hover { background:color-mix(in srgb,var(--sellwerk-color-main) 28%, #ffffff); }

.hero-content { display:flex; align-items:center; gap:1.4rem; }
.hero.rounded { border-radius:2.25rem; }
.role-badge { display:inline-block; margin-left:.5rem; background:rgba(255,255,255,.18); padding:.15rem .55rem; font-size:.55rem; line-height:1; letter-spacing:.5px; text-transform:uppercase; border:1px solid rgba(255,255,255,.35); border-radius:1rem; backdrop-filter:blur(4px); font-weight:600; }
.theme--light .role-badge { background:rgba(0,0,0,.05); border-color:rgba(0,0,0,.15); color:#334155; }
.avatar-shell :deep(img), .avatar-shell :deep(canvas) { width:84px; height:84px; border-radius:50%; box-shadow:none; border:none; }
.avatar-fallback { width:84px; height:84px; border-radius:50%; background:#222; display:flex; align-items:center; justify-content:center; font-size:1.3rem; font-weight:600; color:#eee; box-shadow:none; border:1px solid #444; }
.theme--light .avatar-fallback { background:var(--sellwerk-color-main); }
.user-meta h1 { margin:0 0 .4rem; font-size:1.6rem; }
.user-meta .username { margin:0; font-size:.9rem; font-weight:600; opacity:.85; letter-spacing:.5px; }
.user-meta .email { margin:.15rem 0 0; font-size:.7rem; opacity:.65; }

.settings-page.padded { padding-left:clamp(2.5rem,7.5vw,9rem); padding-right:clamp(2.5rem,7.5vw,9rem); }
.content { padding:1.15rem 0 2.4rem; display:flex; flex-direction:column; gap:1.15rem; }
.panel { background:linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)); padding:1.55rem 1.7rem 1.65rem; border:1px solid rgba(255,255,255,0.09); border-radius:24px; backdrop-filter:blur(10px) saturate(120%); box-shadow:0 18px 40px -18px rgba(0,0,0,.55), 0 4px 16px -8px rgba(0,0,0,.35); }
.theme--light .panel { background:linear-gradient(140deg,#ffffff,#f5f8fb); border:1px solid #d5dce3; box-shadow:0 22px 50px -24px rgba(0,0,0,.20),0 4px 14px -6px rgba(0,0,0,.08); }
.panel-global-widgets { position:relative; }
/* Replaced checklist with pill toggle group */
.toggle-group.pills { display:flex; flex-wrap:wrap; gap:.55rem; }
.pill { border:1px solid #2c3138; background:#11161c; padding:.5rem .85rem; font-size:.58rem; font-weight:600; letter-spacing:.06em; text-transform:uppercase; cursor:pointer; border-radius:9px; color:#d4d9e0; transition:.18s; line-height:1; display:inline-flex; align-items:center; gap:.4rem; position:relative; }
.pill.mode { background:#1b222b; }
.pill:hover { background:#1e262f; }
.pill.active { background:var(--sellwerk-color-main); border-color:var(--sellwerk-color-main); color:#fff; box-shadow:0 0 0 1px var(--sellwerk-color-main) inset, 0 4px 12px -4px color-mix(in srgb,var(--sellwerk-color-main) 65%, #000); }
.pill.active:hover { filter:brightness(1.06); }
.theme--light .pill { background:#fff; border:1px solid #d5dde5; color:#1e293b; }
.theme--light .pill.mode { background:#f1f5f9; }
.theme--light .pill:hover { background:#f1f5f9; }
.theme--light .pill.active { background:var(--sellwerk-color-main); border-color:var(--sellwerk-color-main); color:#fff; }
.pill-separator { width:10px; height:1px; align-self:center; background:linear-gradient(90deg, transparent, rgba(255,255,255,.25), transparent); margin:0 .15rem; }
.theme--light .pill-separator { background:linear-gradient(90deg, transparent, #cfd8e2, transparent); }
.theme--light .panel { border:1px solid #dbe2ea; }
.panel header h2 { margin:0 0 .25rem; font-size:1.05rem; letter-spacing:.5px; }
.panel header p { margin:0 0 1rem; font-size:.7rem; opacity:.7; }

/* Widget Panel refined */
.widget-panel { position:relative; }
.widget-panel .w-head { display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; margin:-.2rem 0 .9rem; }
.widget-panel .h-titles h2 { margin:0; font-size:.95rem; letter-spacing:.4px; }
.widget-panel .h-titles p { margin:.2rem 0 0; font-size:.62rem; opacity:.6; letter-spacing:.4px; text-transform:uppercase; }
.widget-panel .w-actions { display:flex; align-items:center; gap:.4rem; }
.pill.mini { padding:.35rem .5rem; font-size:.5rem; border-radius:7px; }
.pill.mini:disabled { opacity:.35; cursor:not-allowed; }
.pill.mini.vis { font-weight:700; letter-spacing:.08em; }
.widget-panel .w-body { display:grid; gap:.9rem; grid-template-columns:repeat(auto-fill,minmax(210px,1fr)); }

/* Widget ordering */
.widget-order-list { list-style:none; padding:0; margin:0 0 1rem; display:flex; flex-direction:column; gap:.4rem; }
.widget-order-item { display:grid; grid-template-columns:auto 1fr auto; align-items:center; gap:.9rem; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08); padding:.55rem .7rem; border-radius:10px; cursor:default; }
.theme--light .widget-order-item { background:#f1f5f9; border-color:#e2e8f0; }
.widget-order-item .drag-area { display:flex; flex-direction:column; gap:.25rem; }
.widget-order-item .drag-area .mini { background:#334155; color:#fff; border:none; padding:.15rem .35rem; font-size:.55rem; border-radius:4px; cursor:pointer; }
.theme--light .widget-order-item .drag-area .mini { background:#64748b; }
.widget-order-item .drag-area .mini:disabled { opacity:.35; cursor:not-allowed; }
.widget-order-item .meta { display:flex; flex-direction:column; gap:.15rem; }
.widget-order-item .meta strong { font-size:.72rem; letter-spacing:.5px; }
.widget-order-item .meta small { font-size:.55rem; opacity:.6; }
.widget-order-item .vis { display:flex; }

.details-wrapper { display:flex; flex-direction:column; gap:1rem; }
.details-block { border:1px dashed rgba(255,255,255,0.15); padding:1rem 1rem .9rem; border-radius:12px; background:rgba(255,255,255,0.02); }
.theme--light .details-block { background:#fff; border-color:#d9e1e9; }
.details-block h3 { margin:0 0 .75rem; font-size:.75rem; letter-spacing:.6px; text-transform:uppercase; opacity:.8; }
.sub-grid { display:grid; gap:.9rem; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); }
.sub-grid label { font-size:.65rem; }

.switch.sm { transform:scale(.85); transform-origin:right center; }

/* Widget Tiles (new layout) */
.widget-grid { display:grid; gap:1rem; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); margin-top:.75rem; }
.widget-tile { position:relative; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:20px; padding:1rem 1rem 1.2rem; display:flex; flex-direction:column; gap:.5rem; }
.theme--light .widget-tile { background:#f1f5f9; border-color:#e2e8f0; }
.widget-tile .tile-head { display:flex; align-items:flex-start; justify-content:space-between; gap:.75rem; }
.widget-tile h3 { margin:0; font-size:.9rem; letter-spacing:.4px; }
.widget-tile .desc { display:block; font-size:.55rem; letter-spacing:.5px; text-transform:uppercase; opacity:.55; margin-top:.15rem; }
.widget-tile .actions { display:flex; align-items:center; gap:.35rem; }
.widget-tile .order-btn { background:#334155; color:#fff; border:1px solid #475569; padding:.25rem .45rem; font-size:.55rem; line-height:1; border-radius:6px; cursor:pointer; }
.widget-tile .order-btn:disabled { opacity:.35; cursor:not-allowed; }
.theme--light .widget-tile .order-btn { background:#64748b; border-color:#64748b; }
.widget-tile .tile-body { display:grid; gap:.7rem; grid-template-columns:repeat(auto-fill,minmax(120px,1fr)); }
.widget-tile .tile-body label { font-size:.6rem; font-weight:600; letter-spacing:.4px; }
.widget-tile .tile-body label.inline-row { flex-direction:row; display:flex; align-items:center; justify-content:space-between; }
.widget-tile .tile-body input[type='number'], .widget-tile .tile-body select { font-size:.65rem; padding:.35rem .45rem; border-radius:6px; border:1px solid #2c3138; background:#1e252c; color:#fff; }
.theme--light .widget-tile .tile-body input[type='number'], .theme--light .widget-tile .tile-body select { background:#fff; color:#1d2734; border-color:#cfd8e2; }
.widget-tile .foot-hint { grid-column:1 / -1; font-size:.5rem; text-transform:uppercase; letter-spacing:.55px; opacity:.4; }


.card-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:.6rem; }
.card-row { display:flex; align-items:center; justify-content:space-between; gap:1rem; padding:.85rem 1rem; border:1px solid rgba(255,255,255,0.08); border-radius:10px; background:rgba(255,255,255,0.03); }
.theme--light .card-row { background:#f8fafc; border-color:#e2e8f0; }
.row-main { flex:1; }
.row-label { font-weight:600; font-size:.8rem; letter-spacing:.4px; }
.row-desc { font-size:.65rem; opacity:.6; margin-top:.25rem; }
.row-action { display:flex; align-items:center; }

.form-grid { display:grid; gap:1rem; grid-template-columns:repeat(auto-fill,minmax(230px,1fr)); margin-top:.75rem; }
label { font-size:.72rem; display:flex; flex-direction:column; gap:.35rem; font-weight:600; letter-spacing:.4px; }
input[type='number'], select, textarea { background:#111; color:#eee; border:1px solid #2c3138; border-radius:8px; padding:.5rem .6rem; font:inherit; font-size:.75rem; }
.theme--light input[type='number'], .theme--light select, .theme--light textarea { background:#fff; color:#1d2734; border-color:#cfd8e2; }
input[type='range'] { width:100%; }

.switch { position:relative; display:inline-block; width:42px; height:22px; }
.switch input { opacity:0; width:0; height:0; }
.slider { position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0; background:#455161; transition:.25s; border-radius:22px; }
.slider:before { position:absolute; content:""; height:16px; width:16px; left:3px; bottom:3px; background:white; border-radius:50%; transition:.25s; box-shadow:0 2px 6px rgba(0,0,0,.35); }
.switch input:checked + .slider { background:var(--sellwerk-color-main); }
.switch input:checked + .slider:before { transform:translateX(20px); }

.export-actions { display:flex; gap:.6rem; margin:.5rem 0 1rem; flex-wrap:wrap; }
.btn { background:var(--sellwerk-color-main); color:#fff; border:none; padding:.7rem 1.05rem; border-radius:10px; cursor:pointer; font:inherit; font-weight:600; font-size:.72rem; letter-spacing:.55px; box-shadow:0 6px 18px -8px color-mix(in srgb,var(--sellwerk-color-main) 70%, #000); transition:.25s; }
.btn.secondary { background:color-mix(in srgb,var(--sellwerk-color-main) 30%, #1e293b); }
.btn.danger { background:#b91c1c; }
.btn:hover { filter:brightness(1.08); transform:translateY(-1px); box-shadow:0 10px 22px -10px color-mix(in srgb,var(--sellwerk-color-main) 80%, #000); }
.theme--light .btn.secondary { background:color-mix(in srgb,var(--sellwerk-color-main) 20%, #ffffff); }
.export-area { width:100%; font-family:monospace; font-size:.7rem; }
.hint { font-size:.6rem; opacity:.55; margin-top:.25rem; }

@media (max-width: 780px) {
	.hero-content { flex-direction:column; align-items:flex-start; }
	.avatar-shell :deep(img), .avatar-fallback { width:72px; height:72px; }
	.content { padding-top:1rem; }
}
</style>
